(() => {
  const DATE_COUNT = 14;
  const INITIAL_DATE_COUNT = 5;
  const BOOKING_TIME_ZONE = 'Europe/Berlin';
  const pendingAppointmentForms = new WeakMap();
  const availabilityRequests = new WeakMap();
  const initializedBookingLists = new WeakSet();

  const bookingDateParts = () => Object.fromEntries(
    new Intl.DateTimeFormat('en-CA', {
      timeZone: BOOKING_TIME_ZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(new Date())
      .filter((part) => ['year', 'month', 'day', 'hour', 'minute'].includes(part.type))
      .map((part) => [part.type, Number(part.value)])
  );

  const isElapsedBookingSlot = (dateValue, timeValue) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateValue) || !/^\d{2}:\d{2}$/.test(timeValue)) return true;
    const current = bookingDateParts();
    const currentDate = `${current.year}-${String(current.month).padStart(2, '0')}-${String(current.day).padStart(2, '0')}`;
    if (dateValue < currentDate) return true;
    if (dateValue > currentDate) return false;

    const [hour, minute] = timeValue.split(':').map(Number);
    return hour * 60 + minute <= current.hour * 60 + current.minute;
  };

  const bookingDateAtOffset = (parts, offset) => {
    const date = new Date(Date.UTC(parts.year, parts.month - 1, parts.day, 12));
    date.setUTCDate(date.getUTCDate() + offset);
    return date;
  };

  const bookingDateValue = (date) => {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const bookingZoneLabel = (dateValue, locale, fallback) => {
    const [year, month, day] = dateValue.split('-').map(Number);
    const reference = new Date(Date.UTC(year, month - 1, day, 12));
    return new Intl.DateTimeFormat(locale, {
      timeZone: BOOKING_TIME_ZONE,
      timeZoneName: 'short',
    }).formatToParts(reference).find((part) => part.type === 'timeZoneName')?.value || fallback;
  };

  const setControlValue = (control, value) => {
    if (!control) return;
    control.value = value;
    control.setAttribute('value', value);
  };

  const restoreDefaultValues = (planner) => {
    planner.querySelectorAll('[data-bf-default-value]').forEach((control) => {
      setControlValue(control, control.dataset.bfDefaultValue || '');
    });
  };

  const selectedBookingValues = (planner) => ({
    date: planner.querySelector('[data-date][aria-selected="true"]')?.dataset.date || '',
    time: planner.querySelector('[data-bf-time-button][aria-selected="true"]')?.dataset.time || '',
  });

  const bookingListOptions = (list) => [...list.querySelectorAll('button[role="option"]')]
    .filter((option) => option.closest('[role="listbox"]') === list);

  const isBookingOptionAvailable = (option) => Boolean(option?.isConnected
    && !option.disabled
    && option.getAttribute('aria-disabled') !== 'true'
    && !['true', 'pending', 'error'].includes(option.dataset.bfBlocked)
    && !option.closest('[hidden], [inert], [aria-hidden="true"], details:not([open])'));

  const syncBookingList = (list, focusedOption = document.activeElement) => {
    const options = bookingListOptions(list);
    const available = options.filter(isBookingOptionAvailable);
    const entry = available.includes(focusedOption)
      ? focusedOption
      : available.find((option) => option.getAttribute('aria-selected') === 'true') || available[0];
    options.forEach((option) => { option.tabIndex = option === entry ? 0 : -1; });
    return available;
  };

  const syncBookingKeyboard = (planner, focusBeforeUpdate = null) => {
    const lists = [...planner.querySelectorAll('[role="listbox"]')]
      .filter((list) => list.closest('[data-bf-purchase-planner]') === planner);

    lists.forEach((list) => {
      if (!initializedBookingLists.has(list)) {
        initializedBookingLists.add(list);
        list.addEventListener('keydown', (event) => {
          if (event.defaultPrevented || event.isComposing || event.altKey || event.ctrlKey || event.metaKey) return;
          if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
          const option = event.target?.closest('button[role="option"]');
          const available = syncBookingList(list);
          const index = available.indexOf(option);
          if (index < 0 || option.closest('[role="listbox"]') !== list) return;
          const targetIndex = event.key === 'Home' ? 0
            : event.key === 'End' ? available.length - 1
              : Math.max(0, Math.min(available.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1)));
          const target = available[targetIndex];
          event.preventDefault();
          event.stopPropagation();
          syncBookingList(list, target);
          if (document.activeElement !== target) target.focus();
        });
        list.addEventListener('focusin', (event) => {
          const option = event.target?.closest('button[role="option"]');
          if (option?.closest('[role="listbox"]') === list) syncBookingList(list, option);
        });
        list.addEventListener('focusout', (event) => {
          if (!list.contains(event.relatedTarget)) syncBookingList(list, null);
        });
      }
      syncBookingList(list);
    });

    const lostList = focusBeforeUpdate?.closest?.('[role="listbox"]');
    if (!lists.includes(lostList) || isBookingOptionAvailable(focusBeforeUpdate)) return;
    if (document.activeElement !== focusBeforeUpdate && document.activeElement !== document.body) return;
    const options = bookingListOptions(lostList);
    const index = options.indexOf(focusBeforeUpdate);
    if (index < 0) return;
    const next = options.slice(index + 1).find(isBookingOptionAvailable)
      || options.slice(0, index).reverse().find(isBookingOptionAvailable);
    const dateList = planner.querySelector('[data-bf-date-picker]');
    const dates = dateList ? bookingListOptions(dateList).filter(isBookingOptionAvailable) : [];
    const fallback = next || dates.find((option) => option.getAttribute('aria-selected') === 'true') || dates[0];
    if (fallback) {
      syncBookingList(fallback.closest('[role="listbox"]'), fallback);
      fallback.focus();
    }
  };

  const plannerEventContext = (planner) => ({
    product_id: planner.dataset.bfProductId || '',
    product_handle: planner.dataset.bfProductHandle || '',
    variant_id: planner.dataset.bfVariantId || '',
    service_mode: planner.dataset.bfServiceMode || '',
  });

  const publishPlannerEvent = (planner, eventName, eventData = {}) => {
    window.BimmerfitConversion?.publish(eventName, {
      ...plannerEventContext(planner),
      ...eventData,
    });
  };

  const clearBookingError = (planner) => {
    const error = planner.querySelector('[data-bf-booking-error]');
    if (error) {
      error.textContent = error.dataset.bfValidationError || error.textContent;
      error.hidden = true;
    }
    planner.classList.remove('bf-purchase-planner--invalid');
  };

  const showBookingValidation = (planner) => {
    const { date, time } = selectedBookingValues(planner);
    publishPlannerEvent(planner, 'bimmerfit:appointment_validation_error', {
      missing_date: !date,
      missing_time: !time,
    });
    const error = planner.querySelector('[data-bf-booking-error]');
    if (error) {
      error.textContent = error.dataset.bfValidationError || error.textContent;
      error.hidden = false;
    }
    planner.classList.add('bf-purchase-planner--invalid');
    const disclosure = planner.querySelector('[data-bf-booking-disclosure]');
    if (disclosure) disclosure.open = true;
    const focusTarget = date
      ? planner.querySelector('[data-bf-time-button]:not([disabled])')
      : planner.querySelector('[data-date]:not([disabled])');
    focusTarget?.focus();
    planner.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const syncBookingCheckout = (planner) => {
    const { date, time } = selectedBookingValues(planner);
    const complete = Boolean(date && time && !isElapsedBookingSlot(date, time));
    setControlValue(planner.querySelector('[data-bf-appointment-status]'),
      complete ? planner.dataset.bfPendingStatus : planner.dataset.bfDeferredStatus);
    setControlValue(planner.querySelector('[data-bf-appointment-status-machine]'),
      complete ? 'pending' : 'awaiting_coordination');
    const control = planner.querySelector('[data-bf-appointment-date]');
    const acceleratedCheckout = control?.form
      ?.closest('product-form-component')
      ?.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
    if (acceleratedCheckout?.dataset.bfBookingHidden === 'true') {
      acceleratedCheckout.hidden = false;
      delete acceleratedCheckout.dataset.bfBookingHidden;
    }
  };

  const refreshAvailability = async (planner, dateValue) => {
    const endpoint = planner.dataset.bfAvailabilityEndpoint;
    const buttons = [...planner.querySelectorAll('[data-bf-time-button]')];
    if (!endpoint || !dateValue || !buttons.length) return;

    const requestKey = {};
    availabilityRequests.set(planner, requestKey);
    const focusBeforePending = document.activeElement;
    buttons.forEach((button) => {
      button.disabled = true;
      button.dataset.bfBlocked = 'pending';
    });
    syncBookingKeyboard(planner, focusBeforePending);

    try {
      const url = new URL(endpoint, window.location.origin);
      url.searchParams.set('date', dateValue);
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      if (!response.ok) throw new Error(`Availability request failed: ${response.status}`);
      const data = await response.json();
      if (availabilityRequests.get(planner) !== requestKey) return;
      const blocked = new Set(Array.isArray(data.blocked) ? data.blocked : []);
      const focusBeforeResponse = document.activeElement;

      buttons.forEach((button) => {
        const isBlocked = blocked.has(button.dataset.time) || isElapsedBookingSlot(dateValue, button.dataset.time || '');
        button.dataset.bfBlocked = String(isBlocked);
        button.disabled = isBlocked;
        button.setAttribute('aria-disabled', String(isBlocked));
      });

      const selected = planner.querySelector('[data-bf-time-button][aria-selected="true"]');
      if (selected?.disabled) {
        selected.setAttribute('aria-selected', 'false');
        setControlValue(planner.querySelector('[data-bf-appointment-time]'), '');
        setControlValue(planner.querySelector('[data-bf-appointment-time-machine]'), '');
        updateBookingSummary(planner);
      }
      clearBookingError(planner);
      syncBookingCheckout(planner);
      syncBookingKeyboard(planner, focusBeforeResponse);
    } catch (error) {
      if (availabilityRequests.get(planner) !== requestKey) return;
      console.warn('Bimmerfit: Terminverfügbarkeit konnte nicht geladen werden.', error);
      const focusBeforeError = document.activeElement;
      buttons.forEach((button) => {
        button.disabled = true;
        button.dataset.bfBlocked = 'error';
        button.setAttribute('aria-disabled', 'true');
      });
      const message = planner.querySelector('[data-bf-booking-error]');
      if (message) {
        message.textContent = message.dataset.bfAvailabilityError || message.dataset.bfValidationError || message.textContent;
        message.hidden = false;
      }
      planner.classList.add('bf-purchase-planner--invalid');
      syncBookingCheckout(planner);
      syncBookingKeyboard(planner, focusBeforeError);
    }
  };

  const updateBookingSummary = (planner) => {
    const { date: selectedDate, time: selectedTime } = selectedBookingValues(planner);
    const summary = planner.querySelector('[data-bf-booking-summary]');
    if (!summary) return;

    if (!selectedDate || !selectedTime) {
      summary.hidden = true;
      return;
    }

    const [year, month, day] = selectedDate.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day, 12));
    const locale = planner.dataset.bfLocale || 'de-DE';
    const label = new Intl.DateTimeFormat(locale, {
      timeZone: 'UTC',
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    }).format(date);
    const zoneLabel = bookingZoneLabel(selectedDate, locale, planner.dataset.bfTimeZoneLabel || BOOKING_TIME_ZONE);
    summary.textContent = (planner.dataset.bfSummaryTemplate || '{date} · {time} · {zone}')
      .replace('{date}', label)
      .replace('{time}', selectedTime)
      .replace('{zone}', zoneLabel);
    summary.hidden = false;
  };

  const plannerForForm = (form) => {
    if (!(form instanceof HTMLFormElement)) return null;
    const formId = form.getAttribute('id') || '';

    return [...document.querySelectorAll('[data-bf-purchase-planner]')].find((item) => {
      const control = item.querySelector('[data-bf-appointment-date], [data-bf-shipping-control]');
      if (!control) return false;

      return control.form === form || (formId && control.getAttribute('form') === formId);
    });
  };

  const addPlannerProperties = (formData, planner) => {
    const appointmentCard = planner.querySelector('[data-bf-appointment-card]');
    const shippingCard = planner.querySelector('[data-bf-shipping-card]');

    if (!appointmentCard?.hidden) {
      const { date, time } = selectedBookingValues(planner);
      const dateControl = planner.querySelector('[data-bf-appointment-date]');
      const timeControl = planner.querySelector('[data-bf-appointment-time]');
      const timezoneControl = planner.querySelector('[data-bf-appointment-timezone]');
      const statusControl = planner.querySelector('[data-bf-appointment-status]');
      const machineDateControl = planner.querySelector('[data-bf-appointment-date-machine]');
      const machineTimeControl = planner.querySelector('[data-bf-appointment-time-machine]');
      const machineTimezoneControl = planner.querySelector('[data-bf-appointment-timezone-machine]');
      const machineStatusControl = planner.querySelector('[data-bf-appointment-status-machine]');
      const vinControl = planner.querySelector('[data-bf-appointment-vin]');
      const vin = vinControl?.value.trim() || '';
      const complete = Boolean(date && time && !isElapsedBookingSlot(date, time));
      planner.querySelectorAll('[data-bf-appointment-control]').forEach((control) => {
        if (control.name) formData.delete(control.name);
      });
      if (!complete) {
        if (statusControl?.name) formData.set(statusControl.name, planner.dataset.bfDeferredStatus || '');
        if (machineStatusControl?.name) formData.set(machineStatusControl.name, 'awaiting_coordination');
        if (vin && vinControl?.name) formData.set(vinControl.name, vin);
        return;
      }
      if (dateControl?.name) formData.set(dateControl.name, date);
      if (timeControl?.name) formData.set(timeControl.name, time);
      if (timezoneControl?.name) formData.set(timezoneControl.name, timezoneControl.value);
      if (statusControl?.name) formData.set(statusControl.name, planner.dataset.bfPendingStatus || statusControl.value);
      if (machineDateControl?.name) formData.set(machineDateControl.name, date);
      if (machineTimeControl?.name) formData.set(machineTimeControl.name, time);
      if (machineTimezoneControl?.name) formData.set(machineTimezoneControl.name, BOOKING_TIME_ZONE);
      if (machineStatusControl?.name) formData.set(machineStatusControl.name, 'pending');
      if (vin && vinControl?.name) formData.set(vinControl.name, vin);
    }

    if (!shippingCard?.hidden) {
      planner.querySelectorAll('[data-bf-shipping-control][data-bf-default-value]').forEach((control) => {
        formData.set(control.name, control.dataset.bfDefaultValue || '');
      });
    }
  };

  const initBookingPicker = (planner) => {
    if (planner.dataset.bfBookingReady === 'true') return;

    const datePicker = planner.querySelector('[data-bf-date-picker]');
    const dateInput = planner.querySelector('[data-bf-appointment-date]');
    const timeInput = planner.querySelector('[data-bf-appointment-time]');
    const machineDateInput = planner.querySelector('[data-bf-appointment-date-machine]');
    const machineTimeInput = planner.querySelector('[data-bf-appointment-time-machine]');
    const timeStep = planner.querySelector('[data-bf-time-step]');
    const moreDatesButton = planner.querySelector('[data-bf-more-dates]');
    const disclosure = planner.querySelector('[data-bf-booking-disclosure]');
    if (!datePicker || !dateInput || !timeInput || !timeStep || !moreDatesButton) return;

    disclosure?.addEventListener('toggle', () => {
      syncBookingKeyboard(planner);
      if (!disclosure.open || disclosure.dataset.bfTracked === 'true') return;
      disclosure.dataset.bfTracked = 'true';
      publishPlannerEvent(planner, 'bimmerfit:appointment_picker_opened');
    });

    const locale = planner.dataset.bfLocale || 'de-DE';
    const weekdayFormatter = new Intl.DateTimeFormat(locale, { timeZone: 'UTC', weekday: 'short' });
    const monthFormatter = new Intl.DateTimeFormat(locale, { timeZone: 'UTC', month: 'short' });
    const longDateFormatter = new Intl.DateTimeFormat(locale, {
      timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });
    const today = bookingDateParts();

    for (let offset = 0; offset < DATE_COUNT; offset += 1) {
      const date = bookingDateAtOffset(today, offset);
      const value = bookingDateValue(date);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'bf-date-option';
      button.dataset.date = value;
      button.setAttribute('role', 'option');
      button.setAttribute('aria-selected', 'false');
      button.hidden = offset >= INITIAL_DATE_COUNT;
      const shortWeekday = offset === 0 ? planner.dataset.bfTodayLabel || weekdayFormatter.format(date) : weekdayFormatter.format(date);
      const shortMonth = monthFormatter.format(date);
      button.title = longDateFormatter.format(date);
      button.innerHTML = `
        <span class="bf-date-option__weekday">${shortWeekday}</span>
        <strong>${date.getUTCDate()}</strong>
        <span class="bf-date-option__month">${shortMonth}</span>
      `;
      datePicker.append(button);
    }

    moreDatesButton.addEventListener('click', (event) => {
      const moveKeyboardFocus = event.detail === 0 && document.activeElement === moreDatesButton;
      const revealed = [...datePicker.querySelectorAll('[data-date][hidden]')];
      revealed.forEach((button) => {
        button.hidden = false;
      });
      moreDatesButton.setAttribute('aria-expanded', 'true');
      moreDatesButton.hidden = true;
      syncBookingKeyboard(planner);
      const firstRevealed = revealed.find(isBookingOptionAvailable);
      if (moveKeyboardFocus && firstRevealed) {
        syncBookingList(datePicker, firstRevealed);
        firstRevealed.focus();
      }
      publishPlannerEvent(planner, 'bimmerfit:more_dates_opened');
    });

    datePicker.addEventListener('click', (event) => {
      const button = event.target.closest('[data-date]');
      if (!button || button.disabled) return;

      datePicker.querySelectorAll('[data-date]').forEach((item) => {
        item.setAttribute('aria-selected', String(item === button));
      });
      event.preventDefault();
      event.stopPropagation();
      setControlValue(dateInput, button.dataset.date);
      setControlValue(timeInput, '');
      setControlValue(machineDateInput, button.dataset.date);
      setControlValue(machineTimeInput, '');
      planner.querySelectorAll('[data-bf-time-button]').forEach((item) => item.setAttribute('aria-selected', 'false'));
      timeStep.hidden = false;
      clearBookingError(planner);
      updateBookingSummary(planner);
      syncBookingCheckout(planner);
      refreshAvailability(planner, button.dataset.date);
      syncBookingKeyboard(planner);
      publishPlannerEvent(planner, 'bimmerfit:appointment_date_selected');
    });

    planner.querySelectorAll('[data-bf-time-button]').forEach((button) => {
      button.addEventListener('click', (event) => {
        if (button.disabled) return;
        event.preventDefault();
        event.stopPropagation();
        planner.querySelectorAll('[data-bf-time-button]').forEach((item) => {
          item.setAttribute('aria-selected', String(item === button));
        });
        setControlValue(timeInput, button.dataset.time);
        setControlValue(machineTimeInput, button.dataset.time);
        clearBookingError(planner);
        updateBookingSummary(planner);
        syncBookingCheckout(planner);
        syncBookingKeyboard(planner);
        publishPlannerEvent(planner, 'bimmerfit:appointment_time_selected');
      });
    });

    restoreDefaultValues(planner);
    planner.dataset.bfBookingReady = 'true';
    syncBookingKeyboard(planner);
  };

  const readModes = (planner) => {
    const source = planner.nextElementSibling;
    if (!source?.matches('[data-bf-variant-modes]')) return {};

    try {
      return JSON.parse(source.textContent);
    } catch (error) {
      console.warn('Bimmerfit: Variantenregeln konnten nicht gelesen werden.', error);
      return {};
    }
  };

  const selectedVariantId = () => {
    const selectedVariant = document.querySelector('input[type="radio"][data-variant-id]:checked');
    return selectedVariant?.dataset.variantId || new URLSearchParams(window.location.search).get('variant');
  };

  const syncPlanner = (planner, requestedVariantId) => {
    const focusBeforeSync = document.activeElement;
    initBookingPicker(planner);
    const modes = readModes(planner);
    const variantId = requestedVariantId || selectedVariantId() || Object.keys(modes)[0];
    const mode = modes[variantId] || Object.values(modes)[0] || { shipping: false, appointment: true };
    const appointmentCard = planner.querySelector('[data-bf-appointment-card]');
    const shippingCard = planner.querySelector('[data-bf-shipping-card]');

    if (appointmentCard) appointmentCard.hidden = !mode.appointment;
    if (shippingCard) shippingCard.hidden = !mode.shipping;
    planner.dataset.bfVariantId = variantId || '';
    planner.dataset.bfServiceMode = mode.shipping
      ? 'send_in'
      : mode.appointment
        ? 'remote_appointment'
        : 'without_appointment';

    planner.querySelectorAll('[data-bf-appointment-control]').forEach((control) => {
      control.disabled = !mode.appointment;
    });
    planner.querySelectorAll('[data-date], [data-bf-time-button]').forEach((button) => {
      button.disabled = !mode.appointment || ['true', 'pending', 'error'].includes(button.dataset.bfBlocked);
    });
    if (!mode.appointment) clearBookingError(planner);
    planner.querySelectorAll('[data-bf-shipping-control]').forEach((control) => {
      control.disabled = !mode.shipping;
    });
    restoreDefaultValues(planner);
    syncBookingCheckout(planner);
    syncBookingKeyboard(planner, focusBeforeSync);
  };

  const syncAll = (variantId) => {
    document.querySelectorAll('[data-bf-purchase-planner]').forEach((planner) => syncPlanner(planner, variantId));
  };

  const syncAfterThemeUpdate = (variantId) => {
    syncAll(variantId);
    window.setTimeout(() => syncAll(variantId), 0);
    window.setTimeout(() => syncAll(), 500);
  };

  document.addEventListener('change', (event) => {
    if (event.target?.matches('[data-variant-id], select[name^="options"]')) syncAfterThemeUpdate();
  }, true);

  document.addEventListener('bimmerfit:product-submit', (event) => {
    const form = event.detail?.form;
    const planner = plannerForForm(form);
    const appointmentCard = planner?.querySelector('[data-bf-appointment-card]');
    if (!planner || appointmentCard?.hidden) return;
    const { date, time } = selectedBookingValues(planner);
    if (date && time && !isElapsedBookingSlot(date, time)) {
      pendingAppointmentForms.set(form, plannerEventContext(planner));
    } else {
      pendingAppointmentForms.delete(form);
      publishPlannerEvent(planner, 'bimmerfit:appointment_deferred');
    }
  }, true);

  document.addEventListener('shopify:cart:lines-update', (event) => {
    if (event.action !== 'add' || typeof event.promise?.then !== 'function') return;
    const form = event.target instanceof Element ? event.target.querySelector('form') : null;
    const eventData = form ? pendingAppointmentForms.get(form) : null;
    if (!form || !eventData) return;
    pendingAppointmentForms.delete(form);

    event.promise
      .then((result) => {
        if (result?.detail?.didError || result?.userErrors?.length) return;
        window.BimmerfitConversion?.publish('bimmerfit:appointment_added_to_cart', eventData);
      })
      .catch(() => {});
  }, { capture: true });

  // Horizon baut beim Variantenwechsel Teile des Produktformulars neu auf und
  // kann dabei die Werte externer form-gebundener Inputs zurücksetzen. Das
  // formdata-Ereignis läuft direkt beim Erstellen der Warenkorb-Anfrage und
  // stellt sicher, dass genau die sichtbare Auswahl übertragen wird.
  document.addEventListener('formdata', (event) => {
    const planner = plannerForForm(event.target);
    if (planner) addPlannerProperties(event.formData, planner);
  }, true);

  document.addEventListener('shopify:product:select', (event) => {
    syncAfterThemeUpdate(event.detail?.detail?.variantId || event.detail?.variantId);
  });

  new MutationObserver((changes) => {
    if (changes.some((change) => [...change.addedNodes].some((node) =>
      node.nodeType === Node.ELEMENT_NODE && (node.matches?.('[data-bf-purchase-planner]') || node.querySelector?.('[data-bf-purchase-planner]'))
    ))) syncAll();
  }).observe(document.documentElement, { childList: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => syncAll(), { once: true });
  } else {
    syncAll();
  }
})();
